import React from "react"
import { Box, ButtonBase, IconButton, Stack } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { colors, typeScale } from "../theme/tokens"
import { ViraIcon } from "../atoms/ViraIcon"
import { Heading, ViraText } from "../atoms/ViraText"
import {
  ContentCard,
} from "../molecules/ContentCard"

export type CalendarEventKind =
  | "session"
  | "nudge"
  | "assessment"
  | "other"

export type CalendarEvent = {
  id: string
  title: string
  /** ISO date (`YYYY-MM-DD`) or datetime */
  start: string
  end?: string
  kind: CalendarEventKind
  /** Optional secondary line (time, status) */
  meta?: string
}

export type BigCalendarProps = {
  /** Controlled visible month (any date in that month). */
  month?: Date
  /** Uncontrolled initial month. Defaults to today. */
  defaultMonth?: Date
  onMonthChange?: (month: Date) => void
  events?: CalendarEvent[]
  onEventClick?: (event: CalendarEvent) => void
  onDayClick?: (date: Date) => void
  /** Highlighted day as `YYYY-MM-DD`. */
  selectedDate?: string
  loading?: boolean
  /** Card title. Default: "Calendar". */
  title?: string
  /** Max event chips shown per day before "+N more". */
  maxEventsPerDay?: number
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const

const KIND_STYLES: Record<
  CalendarEventKind,
  { bg: string; text: string; label: string }
> = {
  session: {
    bg: colors.vira.lavender,
    text: colors.vira.lavenderText,
    label: "Session",
  },
  nudge: {
    bg: "#FDF0E8",
    text: colors.vira.accent,
    label: "Nudge",
  },
  assessment: {
    bg: colors.vira.chipViraPathBg,
    text: colors.vira.chipViraPathText,
    label: "Assessment",
  },
  other: {
    bg: colors.vira.softSurface,
    text: colors.vira.muted,
    label: "Other",
  },
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1)
}

function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function parseEventDay(start: string): string {
  // Prefer the calendar day in local time for bare dates; for ISO datetimes
  // use the local date so clinician TZ matches the rest of Client Detail.
  if (/^\d{4}-\d{2}-\d{2}$/.test(start)) return start
  const d = new Date(start)
  if (Number.isNaN(d.getTime())) return start.slice(0, 10)
  return toDateKey(d)
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

/** Sunday-start grid covering the visible month (always 6 weeks). */
function buildMonthCells(month: Date): Date[] {
  const first = startOfMonth(month)
  const gridStart = new Date(first)
  gridStart.setDate(first.getDate() - first.getDay())
  const cells: Date[] = []
  for (let i = 0; i < 42; i++) {
    const cell = new Date(gridStart)
    cell.setDate(gridStart.getDate() + i)
    cells.push(cell)
  }
  return cells
}

function groupEventsByDay(
  events: CalendarEvent[],
): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>()
  for (const event of events) {
    const key = parseEventDay(event.start)
    const list = map.get(key) ?? []
    list.push(event)
    map.set(key, list)
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.start.localeCompare(b.start) || a.title.localeCompare(b.title))
  }
  return map
}

/**
 * Full-width month calendar for Client Detail — sessions, nudges,
 * assessments, and other dated items.
 */
export function BigCalendar({
  month: monthProp,
  defaultMonth,
  onMonthChange,
  events = [],
  onEventClick,
  onDayClick,
  selectedDate,
  loading = false,
  title = "Calendar",
  maxEventsPerDay = 3,
}: BigCalendarProps) {
  const isControlled = monthProp !== undefined
  const [uncontrolled, setUncontrolled] = React.useState(
    () => startOfMonth(defaultMonth ?? monthProp ?? new Date()),
  )
  const month = startOfMonth(monthProp ?? uncontrolled)
  const todayKey = toDateKey(new Date())
  const byDay = React.useMemo(() => groupEventsByDay(events), [events])
  const cells = React.useMemo(() => buildMonthCells(month), [month])

  const setMonth = React.useCallback(
    (next: Date) => {
      const normalized = startOfMonth(next)
      if (!isControlled) setUncontrolled(normalized)
      onMonthChange?.(normalized)
    },
    [isControlled, onMonthChange],
  )

  const goToday = () => setMonth(new Date())

  return (
    <ContentCard
      title={title}
      meta={
        <ViraText
          tone="subtle"
          sx={{ fontSize: typeScale.meta, lineHeight: 1.4 }}
        >
          {loading
            ? "Loading…"
            : `${events.length} item${events.length === 1 ? "" : "s"}`}
        </ViraText>
      }
      actions={
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <ButtonBase
            onClick={goToday}
            aria-label="Go to today"
            sx={(theme) => ({
              px: 1.25,
              py: 0.5,
              borderRadius: "6px",
              fontFamily: '"Lato", sans-serif',
              fontSize: typeScale.meta,
              fontWeight: 600,
              color: theme.palette.vira?.ink ?? colors.vira.ink,
              border: `1px solid ${theme.palette.vira?.border ?? colors.vira.border}`,
              "&:hover": {
                backgroundColor:
                  theme.palette.vira?.navActiveBg ?? colors.vira.navActiveBg,
              },
            })}
          >
            Today
          </ButtonBase>
          <IconButton
            size="small"
            aria-label="Previous month"
            onClick={() => setMonth(addMonths(month, -1))}
          >
            <ViraIcon name="chevronLeft" fontSize="small" />
          </IconButton>
          <Heading
            level="card"
            component="span"
            sx={{ minWidth: 160, textAlign: "center" }}
          >
            {monthLabel(month)}
          </Heading>
          <IconButton
            size="small"
            aria-label="Next month"
            onClick={() => setMonth(addMonths(month, 1))}
          >
            <ViraIcon name="chevronRight" fontSize="small" />
          </IconButton>
        </Stack>
      }
      bodySx={{ pt: 1.5, pb: 2 }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
        aria-label="Event types"
      >
        {(Object.keys(KIND_STYLES) as CalendarEventKind[]).map((kind) => (
          <Stack
            key={kind}
            direction="row"
            alignItems="center"
            spacing={0.75}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: KIND_STYLES[kind].bg,
                border: `1px solid ${KIND_STYLES[kind].text}`,
              }}
            />
            <ViraText
              tone="muted"
              sx={{ fontSize: typeScale.meta, lineHeight: 1.2 }}
            >
              {KIND_STYLES[kind].label}
            </ViraText>
          </Stack>
        ))}
      </Stack>

      <Box
        role="grid"
        aria-label={`Calendar for ${monthLabel(month)}`}
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          border: `1px solid ${theme.palette.vira?.border ?? colors.vira.border}`,
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor:
            theme.palette.vira?.border ?? colors.vira.border,
          gap: "1px",
        })}
      >
        {WEEKDAYS.map((day) => (
          <Box
            key={day}
            role="columnheader"
            sx={(theme) => ({
              px: 1,
              py: 1,
              backgroundColor:
                theme.palette.vira?.softSurface ?? colors.vira.softSurface,
              textAlign: "center",
            })}
          >
            <ViraText
              tone="subtle"
              sx={{
                fontSize: typeScale.meta,
                fontWeight: 600,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              {day}
            </ViraText>
          </Box>
        ))}

        {cells.map((cell) => {
          const key = toDateKey(cell)
          const inMonth = isSameMonth(cell, month)
          const isToday = key === todayKey
          const isSelected = selectedDate === key
          const dayEvents = byDay.get(key) ?? []
          const visible = dayEvents.slice(0, maxEventsPerDay)
          const overflow = dayEvents.length - visible.length

          return (
            <Box
              key={key}
              role="gridcell"
              aria-selected={isSelected}
              onClick={() => onDayClick?.(cell)}
              sx={(theme) => ({
                minHeight: { xs: 88, sm: 112, md: 128 },
                p: 0.75,
                backgroundColor: isSelected
                  ? theme.palette.vira?.navActiveBg ?? colors.vira.navActiveBg
                  : inMonth
                    ? theme.palette.background.paper
                    : theme.palette.vira?.pageBg ?? colors.vira.pageBg,
                cursor: onDayClick ? "pointer" : "default",
              })}
            >
              <Box
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  mb: 0.5,
                  borderRadius: "50%",
                  fontFamily: '"Lato", sans-serif',
                  fontSize: typeScale.body,
                  fontWeight: isToday ? 700 : 500,
                  color: isToday
                    ? theme.palette.mode === "dark"
                      ? colors.neutral.space
                      : colors.neutral.white
                    : inMonth
                      ? theme.palette.vira?.ink ?? colors.vira.ink
                      : theme.palette.vira?.disabledText ??
                        colors.vira.disabledText,
                  backgroundColor: isToday
                    ? theme.palette.vira?.primaryBtn ?? colors.vira.primaryBtn
                    : "transparent",
                })}
              >
                {cell.getDate()}
              </Box>

              <Stack spacing={0.5}>
                {visible.map((event) => {
                  const style = KIND_STYLES[event.kind]
                  return (
                    <ButtonBase
                      key={event.id}
                      onClick={(e) => {
                        e.stopPropagation()
                        onEventClick?.(event)
                      }}
                      sx={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        px: 0.75,
                        py: 0.35,
                        borderRadius: "4px",
                        backgroundColor: style.bg,
                        color: style.text,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: typeScale.meta,
                        fontWeight: 600,
                        lineHeight: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "&:hover": { filter: "brightness(0.97)" },
                      }}
                      title={
                        event.meta
                          ? `${event.title} · ${event.meta}`
                          : event.title
                      }
                    >
                      {event.meta ? `${event.meta} ` : null}
                      {event.title}
                    </ButtonBase>
                  )
                })}
                {overflow > 0 ? (
                  <ViraText
                    tone="subtle"
                    sx={{
                      fontSize: typeScale.meta,
                      pl: 0.75,
                      lineHeight: 1.2,
                    }}
                  >
                    +{overflow} more
                  </ViraText>
                ) : null}
              </Stack>
            </Box>
          )
        })}
      </Box>
    </ContentCard>
  )
}
